import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getUrls, deleteUrl } from '../../services/urlsService';
import { Button } from "../../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { YoolTable, MonoText, RowActions, IconButton } from "../../components/ui/YoolTable";
import { Plus } from 'lucide-react';

export default function UrlsList() {
  const navigate = useNavigate();
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [urlToDelete, setUrlToDelete] = useState(null);

  useEffect(() => {
    const fetchUrls = async () => {
      try {
        const data = await getUrls();
        setUrls(data);
      } catch (err) {
        console.error('Error fetching URLs:', err);
        setError('Error al cargar las URLs. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchUrls();
  }, []);

  const handleDeleteClick = (url) => {
    setUrlToDelete(url);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!urlToDelete) return;
    
    try {
      await deleteUrl(urlToDelete.recordId);
      setUrls(urls.filter(url => url.recordId !== urlToDelete.recordId));
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error('Error deleting URL:', err);
      setError('Error al eliminar la URL. Por favor, intente nuevamente.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold" style={{ color: 'var(--yool-black)' }}>URLs</h1>
        <Link to="/admin/urls/new">
          <Button className="flex items-center">
            <Plus className="mr-2 h-4 w-4" />
            Nueva URL
          </Button>
        </Link>
      </div>

      <YoolTable
        columns={[
          { key: 'name', label: 'Nombre', sortable: true },
          { key: 'url', label: 'URL', wrap: true,
            render: u => (
              <a href={u.url} target="_blank" rel="noopener noreferrer"
                style={{ color: 'var(--yool-navy)', textDecoration: 'underline', wordBreak: 'break-all', fontSize: 12 }}
                title={u.url}>
                {u.url.length > 50 ? u.url.substring(0, 50) + '…' : u.url}
              </a>
            )},
          { key: 'type', label: 'Tipo', sortable: true, render: u => <MonoText>{u.type || 'N/A'}</MonoText> },
          { key: 'customDatacodproducto', label: 'Código Producto',
            render: u => u.customDatacodproducto ? <MonoText>{u.customDatacodproducto}</MonoText> : <span style={{ color: 'var(--yool-muted)' }}>—</span> },
          { key: 'acciones', label: '', align: 'right',
            render: u => (
              <RowActions>
                <IconButton icon="edit" title="Editar" onClick={() => navigate(`/admin/urls/${u.recordId}`)} />
                <IconButton icon="trash" title="Eliminar" danger onClick={() => handleDeleteClick(u)} />
              </RowActions>
            )},
        ]}
        rows={loading ? [] : urls}
        rowKey="recordId"
        search={{ placeholder: 'Buscar URL o nombre…', keys: ['name', 'url', 'type'] }}
        empty={loading
          ? { icon: 'refresh', title: 'Cargando…', text: '' }
          : error
            ? { icon: 'search', title: 'Error al cargar', text: error }
            : { icon: 'plus', title: 'Sin URLs registradas', text: 'Crea la primera URL.' }}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la URL 
              "{urlToDelete?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}